import { useCallback, useEffect, useRef, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { useAuth } from "../../../auth/hooks";
import type {
  MonthLandingPoint,
  MonthLandingResponse,
} from "@shared-core/data/api-types";
import {
  MONTH_LANDING_STACK_ORDER,
  NO_MONTH_LANDING_FILTERS,
  filterCaption,
  formatRange,
  formatSnapshotDay,
  formatSnapshotFreshness,
  hasUnbookedBand,
  isFiltered,
  landingForDisplay,
  tailSnapshotDays,
  targetPaceByDay,
  unbookedBandTotal,
  type MonthLandingFilters,
  type MonthLandingMetric,
} from "@shared-core/domain/month-landing";
import {
  quoteLinesCsvFilename,
  quoteLinesToCsv,
} from "@shared-core/domain/month-landing-quote-lines";
import {
  linesCsvFilename,
  linesToCsv,
} from "@shared-core/domain/month-landing-lines";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import {
  MonthLandingQuoteLinesPanel,
  type MonthLandingQuoteLinesState,
} from "./MonthLandingQuoteLinesPanel";
import {
  MonthLandingLayerLinesPanel,
  type MonthLandingLinesState,
} from "./MonthLandingLayerLinesPanel";
import {
  formatCurrencyGbp,
  formatCurrencyGbpCompact,
  formatCurrencyGbpMillions,
} from "@shared-core/domain/formatters";
import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { useCanUploadAccruals } from "../../../auth/hooks";
import { StatCard } from "../../patterns/StatCard";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import { Tabs, TabsList, TabsTrigger } from "../../primitives/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { currentTime } from "@shared-core/domain/current-time";

// MonthLandingTab — the composed /views/insights/month-landing surface. A
// whole-month projection: cumulative invoiced actuals up to the as-of day,
// booked work landing at its due dates beyond it, then (from Phase B) the
// not-yet-booked projection with its confidence range. Clicking a past day on
// the X axis replays the projection exactly as materialised in that day's
// snapshot. The server composes and derives; this only renders.
// Design: docs/features/month-landing-forecast/01_product_brief.md.

// Certainty gradient shared with /plan's LayeredTimelineChart (sequential
// single-hue ramp); unbooked projection is deliberately neutral — it is a
// forecast, not work.
const COLOUR = {
  invoiced: "#be185d", // pink-700
  billedInAdvance: "#db2777", // pink-600 — invoiced, not yet earned
  whites: "#f472b6", // pink-400 — despatched, not yet invoiced
  approved: "#ec4899", // pink-500
  entered: "#f9a8d4", // pink-300
  unbooked: "#d4d4d8", // zinc-300 — projected, not yet booked
  accrual: "#7c3aed", // violet-600 — an accounting overlay, not print work
  range: "rgba(113,113,122,0.18)", // zinc-500 @ 18% — confidence envelope
  projLine: "#52525b", // zinc-600 — projected total
  target: "#404040",
  asOf: "#0891b2",
  axis: "#737373",
  grid: "#e5e5e5",
  surface: "#ffffff",
};

// Display labels for the snapshot's layer names. The names themselves are the
// dbt model's and are never renamed on the wire.
const LAYER_LABEL = {
  invoiced: "Invoiced",
  billed_in_advance: "Billed in advance",
  whites: "Despatched, not invoiced",
  booked_approved: "Due, approved",
  booked_entered: "Due, not approved",
  unbooked: "Projected, not yet booked",
  accrual_adjustment: "Accrual adjustment",
  // Not drawn in the bar — they are never summed into the landing — but both
  // are drillable from their own footnotes below the chart, so both need a
  // label for the panel header.
  wip_carry: "Work in progress, due beyond the month",
  stock_not_invoiced: "Produced into stock, not invoiced",
} as const;

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; view: MonthLandingResponse };

function currentMonthKey(): string {
  const now = currentTime();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * The scope vocabulary the view module speaks: `company`, or `rep:<user_id>`.
 * Mirrors COMPANY_SCOPE_ID and `_parse_scope` in
 * `modules/views_module/actions/get_month_landing.py`.
 */
const COMPANY_SCOPE_ID = "company";
const REP_SCOPE_PREFIX = "rep:";

// The projection band's key in the composition bar. It is not a dbt layer: the
// band is two projection layers, and what is behind it is a list of quotes, not
// jobs — so it drills to the quote panel while every other band drills to the
// layer panel.
const UNBOOKED_BAND_KEY = "unbooked";

export function MonthLandingTab() {
  const adapter = useDataAdapter();
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;
  const [month, setMonth] = useState(currentMonthKey);
  // Company until the first response says this user has a book of their own.
  // The scopes come back with the view, so the default cannot be decided at
  // mount — see the effect below.
  const [scopeId, setScopeId] = useState(COMPANY_SCOPE_ID);
  const ownScopeApplied = useRef(false);
  const [metric, setMetric] = useState<MonthLandingMetric>("revenue");
  // null = latest snapshot; a YYYY-MM-DD = replaying that day.
  const [asOf, setAsOf] = useState<string | null>(null);
  // Display-only lenses. They never change what the server computed — see
  // landingForDisplay.
  const [filters, setFilters] = useState<MonthLandingFilters>(
    NO_MONTH_LANDING_FILTERS,
  );
  const [state, setState] = useState<State>({ status: "loading" });
  // The drill-downs under the chart. `openDrill` is the band being explained —
  // a dbt layer key, or "unbooked" for the projection band, whose answer is a
  // list of quotes rather than jobs. One affordance for every band: click it.
  //
  // Closed until asked for, and dropped whenever the month or scope changes —
  // the rows belong to one (month, scope), and showing the previous
  // selection's under a new heading would be worse than showing none.
  // Replaying a past day only *hides* the panel: the rows are today's either
  // way, so they are still right on the way back.
  const [openDrill, setOpenDrill] = useState<string | null>(null);
  const [quoteLines, setQuoteLines] =
    useState<MonthLandingQuoteLinesState | null>(null);
  const [layerLines, setLayerLines] = useState<MonthLandingLinesState | null>(
    null,
  );
  const canUploadAccruals = useCanUploadAccruals();
  const capabilities = usePlatformCapabilities();

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const view = await adapter.getInsightsMonthLanding({
        month,
        scope: scopeId,
        asOf: asOf ?? undefined,
      });
      setState({ status: "ready", view });
    } catch {
      setState({ status: "error" });
    }
  }, [adapter, month, scopeId, asOf]);

  useEffect(() => {
    load();
  }, [load]);

  const loadQuoteLines = useCallback(async () => {
    setQuoteLines({ status: "loading" });
    try {
      const lines = await adapter.getInsightsMonthLandingQuoteLines({
        month,
        scope: scopeId,
      });
      setQuoteLines({ status: "ready", lines });
    } catch {
      setQuoteLines({ status: "error" });
    }
  }, [adapter, month, scopeId]);

  const loadLayerLines = useCallback(
    async (layer: string) => {
      setLayerLines({ status: "loading" });
      try {
        const lines = await adapter.getInsightsMonthLandingLines({
          month,
          layer,
          scope: scopeId,
        });
        setLayerLines({ status: "ready", lines });
      } catch {
        setLayerLines({ status: "error" });
      }
    },
    [adapter, month, scopeId],
  );

  /** Click a band to explain it; click the open one to close it. */
  const toggleDrill = useCallback(
    (key: string) => {
      setOpenDrill((open) => {
        if (open === key) return null;
        if (key === UNBOOKED_BAND_KEY) {
          if (quoteLines === null) void loadQuoteLines();
        } else {
          void loadLayerLines(key);
        }
        return key;
      });
    },
    [loadLayerLines, loadQuoteLines, quoteLines],
  );

  const downloadLayerLinesCsv = useCallback(() => {
    if (layerLines?.status !== "ready") return;
    capabilities.downloadTextFile(
      linesCsvFilename(month, layerLines.lines.layer, scopeId),
      linesToCsv(layerLines.lines.lines),
      "text/csv",
    );
  }, [capabilities, layerLines, month, scopeId]);

  const downloadQuoteLinesCsv = useCallback(() => {
    if (quoteLines?.status !== "ready") return;
    capabilities.downloadTextFile(
      quoteLinesCsvFilename(month, scopeId),
      quoteLinesToCsv(quoteLines.lines.lines),
      "text/csv",
    );
  }, [capabilities, month, quoteLines, scopeId]);

  /** The rows belong to one (month, scope), so a change to either discards
   *  them rather than leaving the previous selection's on screen. */
  const resetDrill = () => {
    setOpenDrill(null);
    setQuoteLines(null);
    setLayerLines(null);
  };

  /**
   * A rep opens on their own book, not the company's.
   *
   * The rep dropdown is a lens rather than a boundary (spec 06 §8), so this
   * changes where the page starts, never what it will show. It fires once: a
   * rep who deliberately switches to company stays there, and a user with no
   * book of their own never leaves it.
   *
   * It has to be an effect rather than an initial value because the roster of
   * scopes arrives with the first response.
   */
  useEffect(() => {
    if (ownScopeApplied.current) return;
    if (state.status !== "ready" || !currentUserId) return;
    ownScopeApplied.current = true;
    const ownScope = `${REP_SCOPE_PREFIX}${currentUserId}`;
    if (
      scopeId === COMPANY_SCOPE_ID &&
      state.view.available_scopes.some((s) => s.id === ownScope)
    ) {
      setScopeId(ownScope);
    }
  }, [state, currentUserId, scopeId]);

  const selectMonth = (next: string) => {
    setMonth(next);
    setAsOf(null);
    resetDrill();
  };

  const selectScope = (next: string) => {
    setScopeId(next);
    setAsOf(null);
    resetDrill();
  };

  if (state.status === "loading") {
    return (
      <div className="border-border bg-surface text-muted-foreground flex min-h-[420px] items-center justify-center rounded-lg border text-sm">
        Loading month landing…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="border-border bg-surface flex min-h-[420px] flex-col items-center justify-center gap-3 rounded-lg border text-center">
        <p className="text-destructive text-sm">
          Couldn’t load the month landing. Please try again.
        </p>
        <button
          type="button"
          onClick={load}
          className="text-primary text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const view = state.view;
  const series = view.metrics[metric];
  // Replaying = any served day other than the latest. Compared by date, not by
  // day index: every closing-tail snapshot clamps to the last day of the axis,
  // so on a closing month the index cannot tell the 31st from this morning.
  const latestDay =
    view.available_as_of_days[view.available_as_of_days.length - 1] ?? null;
  const isReplaying = asOf !== null && view.as_of_date !== latestDay;
  const tailDays = tailSnapshotDays(view);
  const isTailReplay =
    isReplaying &&
    view.as_of_date !== null &&
    tailDays.includes(view.as_of_date);
  // The served central is the one basis; the filters only change what is SHOWN,
  // and the caption says so whenever they are on.
  const shownLanding = landingForDisplay(series, filters);
  const filtered = isFiltered(filters);
  const caption = filterCaption(series, filters, formatCurrencyGbpCompact);
  const gap = series.target !== null ? shownLanding - series.target : null;
  const freshness = formatSnapshotFreshness(view.snapshot_created_at);
  const hasSnapshot = view.available_as_of_days.length > 0;
  // One day recorded means there is nothing to scrub to, which looks
  // identical to a broken control unless the page says so. Common for a rep
  // whose book was only allocated recently: rep rows are written from the
  // morning the portfolio exists and are never backfilled.
  const hasOnlyOneDay = view.available_as_of_days.length === 1;
  const hasAccrual = series.layers.accrual_adjustment !== 0;
  // The band the composition bar draws and the quote table ties out against —
  // the same helper, so the two can never disagree.
  const unbookedBand = unbookedBandTotal(view, series, filters);

  return (
    <div className="max-w-5xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm">
            Projected month finish from what is invoiced, due and still to book
            — snapshotted daily.
          </p>
          {freshness && (
            <p className="text-muted-foreground text-xs">
              Snapshot {freshness}. Bookings made since are not yet included.
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={month} onValueChange={selectMonth}>
            <SelectTrigger className="w-40" aria-label="Target month">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {view.available_months.map((m) => (
                <SelectItem key={m.month} value={m.month}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {tailDays.length > 0 && (
            // The closing tail's snapshots all sit on the last day of the axis,
            // so the chart cannot scrub between them; this picker can.
            <Select
              value={view.as_of_date ?? ""}
              onValueChange={(d) => setAsOf(d === latestDay ? null : d)}
            >
              <SelectTrigger className="w-44" aria-label="Closing snapshot">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tailDays.map((d) => (
                  <SelectItem key={d} value={d}>
                    Snapshot {formatSnapshotDay(d)}
                  </SelectItem>
                ))}
                {view.as_of_date !== null &&
                  !tailDays.includes(view.as_of_date) && (
                    <SelectItem value={view.as_of_date}>
                      Day {view.as_of_day}
                    </SelectItem>
                  )}
              </SelectContent>
            </Select>
          )}
          <Tabs
            value={metric}
            onValueChange={(v) => setMetric(v as MonthLandingMetric)}
          >
            <TabsList>
              <TabsTrigger value="revenue">Revenue</TabsTrigger>
              <TabsTrigger value="contribution">Contribution</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={scopeId} onValueChange={selectScope}>
            <SelectTrigger className="w-44" aria-label="Scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {view.available_scopes.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Phase 2: cut the same view by work stream / sales nominal.
              Present now so the placement can be reacted to. */}
          <Select value="all" disabled>
            <SelectTrigger
              className="w-48 opacity-60"
              aria-label="Dimension (Phase 2)"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All work · Phase 2</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {!hasSnapshot && (
        <div className="border-border bg-surface flex min-h-[320px] flex-col items-center justify-center gap-2 rounded-lg border text-center">
          <p className="text-sm font-medium">
            No snapshot for {view.month_label} yet.
          </p>
          <p className="text-muted-foreground max-w-md text-sm">
            The month’s picture is recorded once a day, early each morning. The
            first one lands tomorrow — history builds from there and is never
            backfilled.
          </p>
        </div>
      )}

      {hasSnapshot && hasOnlyOneDay && (
        <div className="border-border bg-muted/50 text-muted-foreground rounded-md border px-4 py-2 text-sm">
          One day of history for this view so far, so there is nothing to replay
          yet. A day is added each morning.
          {scopeId !== COMPANY_SCOPE_ID && (
            <>
              {" "}
              The company view usually goes back further —{" "}
              <button
                type="button"
                onClick={() => selectScope(COMPANY_SCOPE_ID)}
                className="text-primary underline hover:no-underline"
              >
                switch to it
              </button>
              .
            </>
          )}
        </div>
      )}

      {hasSnapshot && (
        <>
          {isReplaying && (
            <div className="border-border bg-muted/50 flex items-center justify-between rounded-md border px-4 py-2 text-sm">
              <span>
                {isTailReplay && view.as_of_date ? (
                  <>
                    <span className="font-medium">
                      Viewing {view.month_label} as recorded on{" "}
                      {formatSnapshotDay(view.as_of_date)}
                    </span>
                    , during its closing tail.
                  </>
                ) : (
                  <>
                    <span className="font-medium">
                      Viewing the projection from day {view.as_of_day}
                    </span>{" "}
                    of {view.month_label}, as materialised in that
                    morning&rsquo;s snapshot.
                  </>
                )}
              </span>
              <Button variant="outline" size="sm" onClick={() => setAsOf(null)}>
                Back to {view.phase === "open" ? "today" : "latest"}
              </Button>
            </div>
          )}

          {view.phase === "closing" && !isReplaying && (
            <div className="border-border bg-muted/50 rounded-md border px-4 py-2 text-sm">
              <span className="font-medium">Month closing.</span> Back-dated
              invoices, despatched work converting to invoice and month-end
              adjustments keep landing until{" "}
              {formatSnapshotDay(view.open_until)}. Invoiced so far{" "}
              {formatCurrencyGbpCompact(series.layers.invoiced)}
              {series.layers.whites > 0 && (
                <>
                  , with {formatCurrencyGbpCompact(series.layers.whites)}{" "}
                  despatched and awaiting invoice
                </>
              )}
              . Click a day on the chart to replay how the projection tracked.
            </div>
          )}

          {view.phase === "closed" && !isReplaying && (
            <div className="border-border bg-muted/50 rounded-md border px-4 py-2 text-sm">
              <span className="font-medium">Month closed.</span>{" "}
              {series.target !== null && (
                <>
                  Final invoiced{" "}
                  {formatCurrencyGbpCompact(series.layers.invoiced)} against a{" "}
                  {formatCurrencyGbpCompact(series.target)} target (
                  {series.layers.invoiced >= series.target ? "+" : "−"}
                  {formatCurrencyGbpCompact(
                    Math.abs(series.layers.invoiced - series.target),
                  )}
                  ). Click a day on the chart to replay how the projection
                  tracked.
                </>
              )}
            </div>
          )}

          {series.layers_incomplete && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
              <span className="font-medium">
                Measured on the old definitions.
              </span>{" "}
              This snapshot predates the whites, billed-in-advance and accrual
              layers, so despatched-not-invoiced work still sits inside the
              invoiced and booked figures and those three strips read zero. The
              landing is comparable in total, not layer by layer.
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <LayerFilterToggle
                id="exclude-billed-in-advance"
                checked={filters.excludeBilledInAdvance}
                onCheckedChange={(next) =>
                  setFilters((f) => ({ ...f, excludeBilledInAdvance: next }))
                }
                label="Exclude billed in advance"
                hint={`${formatCurrencyGbpCompact(series.layers.billed_in_advance)} invoiced ahead of finished production`}
              />
              <LayerFilterToggle
                id="hide-accrual-overlay"
                checked={filters.hideAccrualOverlay}
                onCheckedChange={(next) =>
                  setFilters((f) => ({ ...f, hideAccrualOverlay: next }))
                }
                label="Hide accrual overlay"
                hint={
                  hasAccrual
                    ? `${formatCurrencyGbpCompact(Math.abs(series.layers.accrual_adjustment))} ${series.layers.accrual_adjustment < 0 ? "deferred out of" : "brought into"} the month`
                    : "No adjustment uploaded for this month"
                }
              />
            </div>
            {canUploadAccruals && (
              <Link
                to={paths.insightsAccruals()}
                className="text-primary text-sm underline hover:no-underline"
              >
                Upload accruals &amp; deferrals
              </Link>
            )}
          </div>

          {caption && (
            <p className="border-l-2 border-amber-400 pl-3 text-xs text-amber-800">
              {caption} The month&rsquo;s one basis is unchanged.
            </p>
          )}

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard
              label={
                view.phase === "closed" && view.as_of_day >= view.days_in_month
                  ? "Final landing"
                  : "Projected landing"
              }
              value={
                <span>
                  {formatCurrencyGbpMillions(shownLanding)}
                  {filtered && (
                    <span className="text-muted-foreground block text-xs font-normal">
                      filtered view of{" "}
                      {formatCurrencyGbpMillions(series.landing.central)}
                    </span>
                  )}
                  {!filtered &&
                    series.landing.low !== null &&
                    series.landing.high !== null && (
                      <span className="text-muted-foreground block text-xs font-normal">
                        {formatRange(
                          series.landing.low,
                          series.landing.high,
                          formatCurrencyGbpMillions,
                        )}
                      </span>
                    )}
                </span>
              }
            />
            <StatCard
              label="Target"
              value={
                series.target !== null ? (
                  formatCurrencyGbpMillions(series.target)
                ) : (
                  <span className="text-muted-foreground text-base font-normal">
                    Not published
                  </span>
                )
              }
            />
            <StatCard
              label="Gap to target"
              value={
                gap !== null ? (
                  <span>
                    {gap >= 0 ? "+" : "−"}
                    {formatCurrencyGbpMillions(Math.abs(gap))}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-base font-normal">
                    —
                  </span>
                )
              }
            />
            <StatCard
              label="Month elapsed"
              value={
                <span>
                  {view.working_days.elapsed}
                  <span className="text-muted-foreground text-base font-normal">
                    {" "}
                    of {view.working_days.total} working days
                  </span>
                </span>
              }
            />
          </div>

          <CompositionBar
            view={view}
            metric={metric}
            filters={filters}
            openKey={openDrill}
            onDrill={isReplaying ? null : toggleDrill}
          />

          <ProjectionChart
            view={view}
            metric={metric}
            filters={filters}
            onSelectDay={(day) => {
              const candidate = `${view.month}-${String(day).padStart(2, "0")}`;
              // Only days that were actually snapshotted are replayable —
              // gaps are real missed days and must not silently resolve to a
              // neighbouring day's picture.
              if (view.available_as_of_days.includes(candidate)) {
                setAsOf(candidate);
              }
            }}
          />

          {/* Whatever band is open, under the chart. Never beside a replayed
              day: both drills read current state, so they would disagree with
              the snapshot they are explaining. */}
          {isReplaying ? (
            <p className="text-muted-foreground text-xs">
              The jobs and quotes behind each band can only be listed for today
              — both read the book as it stands now, so there is no honest
              version of them for a past day.
            </p>
          ) : (
            <p className="text-muted-foreground text-xs">
              Click a band above to see what is in it.
            </p>
          )}

          {!isReplaying &&
            openDrill === UNBOOKED_BAND_KEY &&
            quoteLines !== null && (
              <MonthLandingQuoteLinesPanel
                state={quoteLines}
                metric={metric}
                bandTotal={unbookedBand}
                bandLabel={LAYER_LABEL.unbooked}
                monthLabel={view.month_label}
                onRetry={loadQuoteLines}
                onDownloadCsv={downloadQuoteLinesCsv}
              />
            )}

          {!isReplaying &&
            openDrill !== null &&
            openDrill !== UNBOOKED_BAND_KEY &&
            layerLines !== null && (
              <MonthLandingLayerLinesPanel
                state={layerLines}
                metric={metric}
                layerLabel={
                  LAYER_LABEL[openDrill as keyof typeof LAYER_LABEL] ??
                  openDrill
                }
                bandTotal={
                  series.layers[openDrill as keyof typeof series.layers] ?? 0
                }
                monthLabel={view.month_label}
                onRetry={() => loadLayerLines(openDrill)}
                onDownloadCsv={downloadLayerLinesCsv}
                onClose={() => setOpenDrill(null)}
              />
            )}

          {series.layers.wip_carry > 0 && (
            <p className="text-muted-foreground text-xs">
              Plus{" "}
              <InformationalDrill
                amount={series.layers.wip_carry}
                layer="wip_carry"
                openKey={openDrill}
                onDrill={isReplaying ? null : toggleDrill}
              />{" "}
              of work in progress due beyond {view.month_label} — context for
              next month, not part of this landing.
            </p>
          )}
          {series.layers.stock_not_invoiced > 0 && (
            <p className="text-muted-foreground text-xs">
              Plus{" "}
              <InformationalDrill
                amount={series.layers.stock_not_invoiced}
                layer="stock_not_invoiced"
                openKey={openDrill}
                onDrill={isReplaying ? null : toggleDrill}
              />{" "}
              of work produced into stock and not yet invoiced — it lands when
              it is called off, so it is not part of this month either.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ── Composition bar ─────────────────────────────────────────────────
// The projected landing's precise makeup at the as-of day, in the fixed
// certainty order, with the remaining gap to target as a dashed segment when
// the projection falls short. The accrual adjustment gets its own strip below:
// it is an accounting overlay rather than a class of print work, and it is
// signed, so it needs a baseline to be read against.

function LayerFilterToggle({
  id,
  checked,
  onCheckedChange,
  label,
  hint,
}: {
  id: string;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <span className="inline-flex items-start gap-2">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onCheckedChange(v === true)}
        aria-describedby={`${id}-hint`}
        className="mt-0.5"
      />
      <span className="leading-tight">
        <label htmlFor={id} className="cursor-pointer">
          {label}
        </label>
        <span id={`${id}-hint`} className="text-muted-foreground block text-xs">
          {hint}
        </span>
      </span>
    </span>
  );
}

/**
 * The drill affordance for a layer that has no segment in the bar.
 *
 * `wip_carry` and `stock_not_invoiced` are informational — never summed into
 * the landing — so they live in footnotes rather than the stack. "What is it
 * made of" is still the same question with the same answer, so the figure in
 * the sentence is the button, and the drill is the one the bands use.
 */
function InformationalDrill({
  amount,
  layer,
  openKey,
  onDrill,
}: {
  amount: number;
  layer: string;
  openKey: string | null;
  onDrill: ((key: string) => void) | null;
}) {
  const label = formatCurrencyGbpCompact(amount);
  if (!onDrill) return <>{label}</>;
  return (
    <button
      type="button"
      onClick={() => onDrill(layer)}
      aria-pressed={openKey === layer}
      aria-label={`${LAYER_LABEL[layer as keyof typeof LAYER_LABEL]}: ${label} — show what is in it`}
      className={
        "underline decoration-dotted underline-offset-2 transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:outline-none " +
        (openKey === layer ? "text-foreground font-medium" : "")
      }
    >
      {label}
    </button>
  );
}

function CompositionBar({
  view,
  metric,
  filters,
  openKey,
  onDrill,
}: {
  view: MonthLandingResponse;
  metric: MonthLandingMetric;
  filters: MonthLandingFilters;
  /** The band currently being explained below, or null. */
  openKey: string | null;
  /** Null while replaying: both drills read current state, so there is no
   *  honest answer for a past day and the bar must not invite one. */
  onDrill: ((key: string) => void) | null;
}) {
  const series = view.metrics[metric];
  const { layers } = series;
  const target = series.target;
  // Once the month has closed (its closing tail over), the landing is
  // invoiced-only — booked and despatched residue that never invoiced into
  // the month has rolled forward, so it leaves the bar and is called out in
  // the caption instead. A closing month still shows the whole stack: its
  // whites are converting and its picture is still moving.
  const atClose =
    view.phase === "closed" && view.as_of_day >= view.days_in_month;
  const shown = landingForDisplay(series, filters);
  // A filtered-out layer leaves the bar entirely rather than being drawn at
  // zero width — the caption above already says what was removed.
  const hidden = new Set<string>(
    filters.excludeBilledInAdvance ? ["billed_in_advance"] : [],
  );
  // Whatever the served landing holds beyond the named layers is the B2
  // not-yet-booked band — one definition in shared-core, because the quote
  // drill-down below ties its table out against this same figure.
  const unbooked = unbookedBandTotal(view, series, filters);
  // The gap to target is measured against the DISPLAYED landing (accrual
  // included), which is the honest economic gap; the bar itself only draws the
  // certainty stack, because the accrual has its own signed strip below.
  const projected = atClose ? layers.invoiced : shown;
  const shortOfTarget = target !== null ? Math.max(0, target - projected) : 0;
  const overTarget = target !== null ? Math.max(0, projected - target) : 0;
  const unlandedResidue = atClose
    ? layers.whites + layers.booked_approved + layers.booked_entered
    : 0;

  const SEGMENT_COLOUR: Record<
    (typeof MONTH_LANDING_STACK_ORDER)[number],
    string
  > = {
    invoiced: COLOUR.invoiced,
    billed_in_advance: COLOUR.billedInAdvance,
    whites: COLOUR.whites,
    booked_approved: COLOUR.approved,
    booked_entered: COLOUR.entered,
  };

  const segments = (
    atClose
      ? [
          {
            key: "invoiced" as const,
            value: layers.invoiced,
            colour: COLOUR.invoiced,
          },
        ]
      : [
          ...MONTH_LANDING_STACK_ORDER.filter((key) => !hidden.has(key)).map(
            (key) => ({
              key,
              value: layers[key],
              colour: SEGMENT_COLOUR[key],
            }),
          ),
          {
            key: "unbooked" as const,
            value: unbooked,
            colour: COLOUR.unbooked,
          },
        ]
  ).filter((seg) => seg.value > 0);

  // Scale by what is actually drawn, so the widths always sum to at most the
  // full bar. Scaling by the landing instead would overflow whenever a net
  // deferral made the stack larger than it — and `overflow-hidden` would clip
  // the last segment silently.
  const barTotal = segments.reduce((sum, seg) => sum + seg.value, 0);
  const scaleMax = barTotal + shortOfTarget;
  if (scaleMax <= 0) return null;
  const pct = (v: number) => `${(Math.abs(v) / scaleMax) * 100}%`;

  const accrual = layers.accrual_adjustment;
  const showAccrualStrip =
    !atClose && !filters.hideAccrualOverlay && accrual !== 0;

  return (
    <div className="space-y-1.5">
      <div className="flex h-6 w-full overflow-hidden rounded-md">
        {segments.map((s) =>
          onDrill ? (
            // A button, not a div with a click handler: the bands are the
            // drill affordance for the whole chart, so they have to be
            // reachable by keyboard and announce their state.
            <button
              key={s.key}
              type="button"
              onClick={() => onDrill(s.key)}
              aria-pressed={openKey === s.key}
              aria-label={`${LAYER_LABEL[s.key]}: ${formatCurrencyGbpCompact(s.value)} — show what is in it`}
              title={`${LAYER_LABEL[s.key]}: ${formatCurrencyGbpCompact(s.value)} — click to see what is in it`}
              className={
                "h-full border-r-2 border-white transition-opacity last:border-r-0 hover:opacity-80 focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:outline-none " +
                (openKey === s.key ? "ring-2 ring-neutral-900" : "")
              }
              style={{ width: pct(s.value), backgroundColor: s.colour }}
            />
          ) : (
            <div
              key={s.key}
              className="h-full border-r-2 border-white last:border-r-0"
              style={{ width: pct(s.value), backgroundColor: s.colour }}
              title={`${LAYER_LABEL[s.key]}: ${formatCurrencyGbpCompact(s.value)}`}
            />
          ),
        )}
        {shortOfTarget > 0 && (
          <div
            className="h-full rounded-r-md border-2 border-dashed border-neutral-300"
            style={{ width: pct(shortOfTarget) }}
            title={`Short of target: ${formatCurrencyGbpCompact(shortOfTarget)}`}
          />
        )}
      </div>

      {showAccrualStrip && (
        <AccrualStrip
          amount={accrual}
          // Half the bar's scale: the strip runs from a centre baseline, so
          // full deflection either way is one landing's worth. Capped so an
          // adjustment larger than the landing still renders inside the strip.
          width={`${Math.min(50, (Math.abs(accrual) / scaleMax) * 50)}%`}
        />
      )}

      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {segments.map((s) => {
          const swatch = (
            <>
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: s.colour }}
              />
              {LAYER_LABEL[s.key]}{" "}
              <span className="text-foreground font-medium">
                {formatCurrencyGbpCompact(s.value)}
              </span>
            </>
          );
          // The legend is the second way to reach the same drill, for the
          // bands too thin to click comfortably — not a second mechanism, the
          // same one with a bigger target.
          return onDrill ? (
            <button
              key={s.key}
              type="button"
              onClick={() => onDrill(s.key)}
              aria-pressed={openKey === s.key}
              className={
                "hover:text-foreground inline-flex items-center gap-1 rounded px-1 py-0.5 transition-colors focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:outline-none " +
                (openKey === s.key ? "bg-muted text-foreground" : "")
              }
            >
              {swatch}
            </button>
          ) : (
            <span key={s.key} className="inline-flex items-center gap-1">
              {swatch}
            </span>
          );
        })}
        {shortOfTarget > 0 && (
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-sm border border-dashed border-neutral-400" />
            Short of target{" "}
            <span className="text-foreground font-medium">
              {formatCurrencyGbpCompact(shortOfTarget)}
            </span>
          </span>
        )}
        {overTarget > 0 && (
          <span className="text-foreground font-medium">
            +{formatCurrencyGbpCompact(overTarget)} over target
          </span>
        )}
        {unlandedResidue > 0 && (
          <span className="italic">
            {formatCurrencyGbpCompact(unlandedResidue)} of booked and despatched
            work never invoiced into the month and rolled forward.
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Accounts' month-end adjustment, drawn against its own baseline so the sign is
 * legible: brought-forward extends right, deferred extends left. Never clamped
 * — a net-deferral month must read as one.
 */
function AccrualStrip({ amount, width }: { amount: number; width: string }) {
  const isDeferral = amount < 0;
  const label = `${LAYER_LABEL.accrual_adjustment}: ${isDeferral ? "−" : "+"}${formatCurrencyGbpCompact(Math.abs(amount))}`;
  return (
    <div className="space-y-1">
      <div
        className="relative h-3 w-full"
        role="img"
        aria-label={`${label} (${isDeferral ? "deferred out of" : "brought into"} the month)`}
      >
        {/* The zero baseline the strip is read against. */}
        <div className="absolute inset-y-0 left-1/2 w-px bg-neutral-400" />
        <div
          className="absolute inset-y-0"
          style={{
            width,
            backgroundColor: COLOUR.accrual,
            // Hatched so an accounting overlay never reads as print work.
            backgroundImage:
              "repeating-linear-gradient(45deg, rgba(255,255,255,0.55) 0 2px, transparent 2px 5px)",
            ...(isDeferral ? { right: "50%" } : { left: "50%" }),
          }}
          title={label}
        />
      </div>
      <p className="text-muted-foreground text-xs">
        <span
          className="mr-1 inline-block h-2.5 w-2.5 rounded-sm align-middle"
          style={{
            backgroundColor: COLOUR.accrual,
            backgroundImage:
              "repeating-linear-gradient(45deg, rgba(255,255,255,0.55) 0 2px, transparent 2px 5px)",
          }}
        />
        {LAYER_LABEL.accrual_adjustment}{" "}
        <span className="text-foreground font-medium">
          {isDeferral ? "−" : "+"}
          {formatCurrencyGbpCompact(Math.abs(amount))}
        </span>{" "}
        — accounts&rsquo; month-end{" "}
        {isDeferral ? "deferral out of" : "amount brought into"} the month, and
        already inside the landing above.
      </p>
    </div>
  );
}

// ── Projection chart ────────────────────────────────────────────────
// Whole month on the X axis. Left of as-of: cumulative invoiced actuals.
// Right of as-of: stacked projection (invoiced base → approved → entered
// → unbooked) with the confidence envelope and central projected line.
// Clicking a snapshotted day replays that day's picture.

function ProjectionChart({
  view,
  metric,
  filters,
  onSelectDay,
}: {
  view: MonthLandingResponse;
  metric: MonthLandingMetric;
  filters: MonthLandingFilters;
  onSelectDay: (day: number) => void;
}) {
  const series = view.metrics[metric];
  const showUnbooked = hasUnbookedBand(view);
  // billed_in_advance and accrual_adjustment are month-level statements with no
  // per-day series, so the chart draws them as flat bands from their RETURNED
  // layer totals. That is rendering a served number, not recomputing one.
  const billedInAdvance = filters.excludeBilledInAdvance
    ? 0
    : series.layers.billed_in_advance;
  const accrual = filters.hideAccrualOverlay
    ? 0
    : series.layers.accrual_adjustment;
  // Where the month should have reached by each day to finish on target —
  // spread over working days, so the line is level across the same days the
  // chart shades rather than climbing over a weekend nothing invoices on.
  const pace = targetPaceByDay(
    series.target,
    view.days_in_month,
    view.non_working_days,
  );
  const points = series.points.map((p) => {
    const isProjection = p.proj_total !== null;
    // What the filtered view projects: the SERVED proj_total minus the served
    // layer totals the filters removed. Same rule as the landing — subtract
    // what came back, never re-derive it from the stack (a second arithmetic
    // path here would be free to drift from the server's).
    const shown =
      p.proj_total === null
        ? null
        : p.proj_total -
          (series.layers.billed_in_advance - billedInAdvance) -
          (series.layers.accrual_adjustment - accrual);
    // The stack top the accrual overlay is measured from, so the band's outer
    // edge always coincides with the projected line.
    const stackTop = shown === null ? null : shown - accrual;
    return {
      ...p,
      proj_billed_in_advance: isProjection ? billedInAdvance : null,
      // The overlay is a signed band between the stack top and the projected
      // total: it grows upward for a brought-forward month and eats downward
      // into the stack for a deferral. Never clamped.
      accrual_band:
        isProjection && accrual !== 0 && stackTop !== null
          ? ([
              Math.min(stackTop, stackTop + accrual),
              Math.max(stackTop, stackTop + accrual),
            ] as [number, number])
          : null,
      // Drives the dashed line and the tooltip's total.
      proj_shown: shown,
      target_pace: pace[p.day - 1] ?? null,
      // recharts reads the envelope as a [low, high] tuple per point.
      total_range:
        p.range_low !== null && p.range_high !== null
          ? ([p.range_low, p.range_high] as [number, number])
          : null,
    };
  });
  const yTop = Math.max(
    series.target ?? 0,
    series.landing.high ?? series.landing.central,
    ...points.map((p) => Math.max(p.invoiced ?? 0, p.proj_shown ?? 0)),
  );
  const replayable = view.available_as_of_days.some((d) =>
    d.startsWith(view.month),
  );

  return (
    <div className="space-y-2">
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart
          data={points}
          margin={{ top: 16, right: 28, bottom: 4, left: 0 }}
          onClick={(state) => {
            const day = Number(state?.activeLabel);
            if (Number.isFinite(day)) onSelectDay(day);
          }}
          style={{ cursor: replayable ? "pointer" : "default" }}
        >
          <CartesianGrid stroke={COLOUR.grid} vertical={false} />
          {/* Weekends and bank holidays, shaded: a flat run on the projected
              line then reads as a non-working day rather than a stall. */}
          {view.non_working_days.map((day) => (
            <ReferenceArea
              key={`nwd-${day}`}
              x1={day - 0.5}
              x2={day + 0.5}
              fill={COLOUR.axis}
              fillOpacity={0.08}
              stroke="none"
            />
          ))}
          <XAxis
            dataKey="day"
            type="number"
            domain={[0.5, view.days_in_month + 0.5]}
            ticks={Array.from({ length: view.days_in_month }, (_, i) => i + 1)}
            tick={(props: {
              x?: number | string;
              y?: number | string;
              payload?: { value?: number | string };
            }) => {
              const day = Number(props.payload?.value);
              const offDay = view.non_working_days.includes(day);
              return (
                <text
                  x={Number(props.x)}
                  y={Number(props.y) + 10}
                  textAnchor="middle"
                  fontSize={10}
                  fill={offDay ? COLOUR.projLine : COLOUR.axis}
                  fontWeight={offDay ? 600 : 400}
                >
                  {day}
                </text>
              );
            }}
            axisLine={{ stroke: COLOUR.grid }}
            tickLine={false}
            interval={0}
            label={{
              value: replayable
                ? `Day of ${view.month_label} — click a past day to replay its snapshot`
                : `Day of ${view.month_label}`,
              position: "insideBottom",
              offset: 0,
              fontSize: 10,
              fill: COLOUR.axis,
            }}
            height={40}
          />
          <YAxis
            tickFormatter={formatCurrencyGbpCompact}
            tick={{ fontSize: 10, fill: COLOUR.axis }}
            axisLine={false}
            tickLine={false}
            width={52}
            domain={[0, Math.ceil((yTop * 1.06) / 50_000) * 50_000]}
          />
          <Tooltip
            content={
              ((props: TooltipContentProps<number, string>) => (
                <ProjectionTooltip
                  {...props}
                  showUnbooked={showUnbooked}
                  billedInAdvance={billedInAdvance}
                  accrual={accrual}
                />
              )) as never
            }
          />

          {/* Confidence envelope behind everything on the projection side. */}
          <Area
            dataKey="total_range"
            fill={COLOUR.range}
            stroke="none"
            fillOpacity={1}
            isAnimationActive={false}
            activeDot={false}
            connectNulls={false}
          />

          {/* Actual invoiced, up to the as-of day. */}
          <Area
            dataKey="invoiced"
            fill={COLOUR.invoiced}
            stroke={COLOUR.surface}
            strokeWidth={2}
            fillOpacity={1}
            isAnimationActive={false}
            connectNulls={false}
          />

          {/* Projection stack from the as-of day. */}
          <Area
            dataKey="proj_invoiced_base"
            stackId="proj"
            fill={COLOUR.invoiced}
            stroke="none"
            fillOpacity={0.55}
            isAnimationActive={false}
            connectNulls={false}
          />
          {/* Flat band — a month-level total, not a per-day series. */}
          <Area
            dataKey="proj_billed_in_advance"
            stackId="proj"
            fill={COLOUR.billedInAdvance}
            stroke={COLOUR.surface}
            strokeWidth={1.5}
            fillOpacity={0.85}
            isAnimationActive={false}
            connectNulls={false}
          />
          <Area
            dataKey="proj_whites"
            stackId="proj"
            fill={COLOUR.whites}
            stroke={COLOUR.surface}
            strokeWidth={1.5}
            fillOpacity={0.85}
            isAnimationActive={false}
            connectNulls={false}
          />
          <Area
            dataKey="proj_approved"
            stackId="proj"
            fill={COLOUR.approved}
            stroke={COLOUR.surface}
            strokeWidth={1.5}
            fillOpacity={0.85}
            isAnimationActive={false}
            connectNulls={false}
          />
          <Area
            dataKey="proj_entered"
            stackId="proj"
            fill={COLOUR.entered}
            stroke={COLOUR.surface}
            strokeWidth={1.5}
            fillOpacity={0.85}
            isAnimationActive={false}
            connectNulls={false}
          />
          {/* Hidden until the Phase-B projection layers arrive — an empty band
              reads as "nothing left to book", which is not what it means. */}
          {showUnbooked && (
            <Area
              dataKey="proj_unbooked"
              stackId="proj"
              fill={COLOUR.unbooked}
              stroke={COLOUR.surface}
              strokeWidth={1.5}
              fillOpacity={0.8}
              isAnimationActive={false}
              connectNulls={false}
            />
          )}

          {/* Accounts' month-end adjustment, as a signed band between the
              stack top and the projected total. Hatched so it never reads as
              another class of print work, and unclamped so a net deferral
              visibly eats into the month. */}
          <defs>
            <pattern
              id="month-landing-accrual-hatch"
              width="6"
              height="6"
              patternTransform="rotate(45)"
              patternUnits="userSpaceOnUse"
            >
              <rect width="6" height="6" fill={COLOUR.accrual} opacity="0.35" />
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="6"
                stroke={COLOUR.accrual}
                strokeWidth="2.5"
              />
            </pattern>
          </defs>
          <Area
            dataKey="accrual_band"
            fill="url(#month-landing-accrual-hatch)"
            stroke={COLOUR.accrual}
            strokeWidth={1}
            fillOpacity={1}
            isAnimationActive={false}
            activeDot={false}
            connectNulls={false}
          />

          {/* Central projected total. */}
          <Line
            dataKey="proj_shown"
            stroke={COLOUR.projLine}
            strokeWidth={1.6}
            strokeDasharray="4 3"
            dot={false}
            isAnimationActive={false}
            connectNulls={false}
          />

          {/* The target as a pace, not a ceiling. A flat line at the month's
              total only answers "did we finish on target" on the last day; it
              says nothing on the 15th, which is when anyone can still act on
              it. Sloped, the vertical distance to the stack IS the gap today.
              The month's total is still on the Target card above, so nothing
              is lost by dropping the horizontal line. */}
          {series.target !== null && (
            <Line
              dataKey="target_pace"
              stroke={COLOUR.target}
              strokeWidth={1.4}
              // A LONG dash, against the projected line's short "4 3". Both
              // lines now climb, so shape is the only cue left: while the
              // target was horizontal its angle told them apart at a glance,
              // and the two greys (#404040 vs #52525b) never did. Dash length
              // also survives being printed or colourblind, which hue would
              // not.
              strokeDasharray="10 5"
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          )}
          {view.as_of_day >= 1 && (
            <ReferenceLine
              x={view.as_of_day}
              stroke={COLOUR.asOf}
              strokeDasharray="3 3"
              strokeWidth={1.5}
              label={{
                value:
                  view.as_of_day === view.today_day && view.phase === "open"
                    ? "This morning"
                    : `Day ${view.as_of_day}`,
                position: "top",
                fill: COLOUR.asOf,
                fontSize: 10,
              }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 pl-2 text-xs">
        <LegendSwatch fill={COLOUR.invoiced} label={LAYER_LABEL.invoiced} />
        {billedInAdvance > 0 && (
          <LegendSwatch
            fill={COLOUR.billedInAdvance}
            label={LAYER_LABEL.billed_in_advance}
          />
        )}
        <LegendSwatch fill={COLOUR.whites} label={LAYER_LABEL.whites} />
        <LegendSwatch
          fill={COLOUR.approved}
          label={LAYER_LABEL.booked_approved}
        />
        <LegendSwatch
          fill={COLOUR.entered}
          label={LAYER_LABEL.booked_entered}
        />
        {showUnbooked && (
          <LegendSwatch fill={COLOUR.unbooked} label={LAYER_LABEL.unbooked} />
        )}
        {accrual !== 0 && (
          <LegendSwatch
            fill={COLOUR.accrual}
            hatched
            label={`${LAYER_LABEL.accrual_adjustment} (${accrual < 0 ? "deferral" : "brought forward"})`}
          />
        )}
        <span className="inline-flex items-center gap-1">
          <svg width="14" height="6" viewBox="0 0 14 6" aria-hidden="true">
            <line
              x1="0"
              y1="3"
              x2="14"
              y2="3"
              stroke={COLOUR.projLine}
              strokeWidth="1.6"
              strokeDasharray="4 3"
            />
          </svg>
          Projected landing
        </span>
        {series.target !== null ? (
          <span className="inline-flex items-center gap-1">
            <svg width="14" height="6" viewBox="0 0 14 6" aria-hidden="true">
              <line
                x1="0"
                y1="3"
                x2="14"
                y2="3"
                stroke={COLOUR.target}
                strokeWidth="1.4"
                strokeDasharray="10 5"
              />
            </svg>
            Target pace
          </span>
        ) : (
          <span className="italic">No target published for this month.</span>
        )}
      </div>
    </div>
  );
}

function ProjectionTooltip(
  props: TooltipContentProps<number, string> & {
    showUnbooked: boolean;
    billedInAdvance: number;
    accrual: number;
  },
) {
  if (!props.active || !props.payload || props.payload.length === 0)
    return null;
  const datum = props.payload[0]?.payload as
    | (MonthLandingPoint & {
        proj_shown: number | null;
        target_pace: number | null;
      })
    | undefined;
  if (!datum) return null;
  const isProjection = datum.proj_total !== null;
  // The invoiced actual is served as ``invoiced`` up to the as-of day and as
  // the stack's ``proj_invoiced_base`` from it; the projected total includes
  // it either way, so the row must show on future days too or the rows will
  // not add up to the total.
  const invoicedToDate = datum.invoiced ?? datum.proj_invoiced_base;
  // Whole pounds, thousands-separated, in an aligned label | value grid: the
  // finance reader reconciles these figures against a spreadsheet, and a
  // compact "£1.1m" beside "£386.9k" cannot be added up or checked.
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded border px-2 py-1 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="font-semibold">Day {datum.day}</div>
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-2 gap-y-0.5 tabular-nums">
        {invoicedToDate !== null && (
          <TooltipRow
            swatch={COLOUR.invoiced}
            label="Invoiced to date"
            value={formatCurrencyGbp(invoicedToDate)}
          />
        )}
        {isProjection && (
          <>
            {props.billedInAdvance > 0 && (
              <TooltipRow
                swatch={COLOUR.billedInAdvance}
                label={LAYER_LABEL.billed_in_advance}
                value={formatCurrencyGbp(props.billedInAdvance)}
              />
            )}
            <TooltipRow
              swatch={COLOUR.whites}
              label={LAYER_LABEL.whites}
              value={formatCurrencyGbp(datum.proj_whites ?? 0)}
            />
            <TooltipRow
              swatch={COLOUR.approved}
              label={LAYER_LABEL.booked_approved}
              value={formatCurrencyGbp(datum.proj_approved ?? 0)}
            />
            <TooltipRow
              swatch={COLOUR.entered}
              label={LAYER_LABEL.booked_entered}
              value={formatCurrencyGbp(datum.proj_entered ?? 0)}
            />
            {props.showUnbooked && (
              <TooltipRow
                swatch={COLOUR.unbooked}
                label={LAYER_LABEL.unbooked}
                value={formatCurrencyGbp(datum.proj_unbooked ?? 0)}
              />
            )}
            {props.accrual !== 0 && (
              <TooltipRow
                swatch={COLOUR.accrual}
                label={LAYER_LABEL.accrual_adjustment}
                value={`${props.accrual < 0 ? "−" : "+"}${formatCurrencyGbp(Math.abs(props.accrual))}`}
              />
            )}
            <TooltipRow
              label="Total projected"
              value={formatCurrencyGbp(datum.proj_shown ?? 0)}
              emphasis
            />
          </>
        )}
        {/* The number behind the gap the sloped line draws. Measured against
            whichever figure that day actually shows — invoiced on a past day,
            the projected total on a future one — so the row always answers
            "against what the chart is showing me here". */}
        {datum.target_pace !== null &&
          (() => {
            const actual = isProjection ? datum.proj_shown : invoicedToDate;
            if (actual === null) return null;
            const delta = actual - datum.target_pace;
            return (
              <>
                <TooltipRow
                  label="Target pace"
                  value={formatCurrencyGbp(datum.target_pace)}
                />
                <TooltipRow
                  label={delta < 0 ? "Behind pace" : "Ahead of pace"}
                  value={`${delta < 0 ? "−" : "+"}${formatCurrencyGbp(Math.abs(delta))}`}
                  emphasis
                />
              </>
            );
          })()}
      </div>
      {isProjection &&
        datum.range_low !== null &&
        datum.range_high !== null && (
          <div className="text-muted-foreground mt-0.5">
            Range{" "}
            {formatRange(datum.range_low, datum.range_high, formatCurrencyGbp)}
          </div>
        )}
    </div>
  );
}

function TooltipRow({
  swatch,
  label,
  value,
  emphasis = false,
}: {
  swatch?: string;
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  // Three grid cells per row (swatch | label | value) so every value lines up
  // in its own right-aligned column; the total carries the border and weight.
  const rowClass = emphasis
    ? "border-border mt-1 border-t pt-1 font-semibold"
    : "";
  return (
    <>
      <span className={rowClass}>
        {swatch && (
          <span
            className="inline-block h-2 w-2 rounded-sm"
            style={{ backgroundColor: swatch }}
          />
        )}
      </span>
      <span className={rowClass}>{label}</span>
      <span className={`${rowClass} text-right`}>{value}</span>
    </>
  );
}

function LegendSwatch({
  fill,
  label,
  hatched = false,
}: {
  fill: string;
  label: string;
  hatched?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="inline-block h-3 w-3 rounded-sm"
        style={{
          backgroundColor: fill,
          ...(hatched
            ? {
                backgroundImage:
                  "repeating-linear-gradient(45deg, rgba(255,255,255,0.55) 0 2px, transparent 2px 5px)",
              }
            : {}),
        }}
      />
      {label}
    </span>
  );
}
